import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../context/API';
import notification from '../commonComponents/Notification/Notification';
import type {
  Merchant,
  Device,
  Transaction,
  PerformanceMetrics,
} from '../components/Reports/AdminReports/types';

export interface AdminReportsState {
  merchants: Merchant[];
  devices: Device[];
  totalMerchants: number;
  activeDevices: number;
  supportTickets: number;
  totalRevenue: number;
  totalOrders: number;
  recentTransactions: Transaction[];
  performanceMetrics: PerformanceMetrics;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: AdminReportsState = {
  merchants: [],
  devices: [],
  totalMerchants: 0,
  activeDevices: 0,
  supportTickets: 0,
  totalRevenue: 0,
  totalOrders: 0,
  recentTransactions: [],
  performanceMetrics: {
    conversionRate: 0,
    averageOrderValue: 0,
    customerSatisfaction: 0,
    systemUptime: 0,
  },
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunk to fetch all admin reports data
export const fetchAdminReports = createAsyncThunk(
  'adminReports/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const [
        merchantsResponse,
        devicesResponse,
        ordersResponse,
        revenueResponse,
        transactionsResponse,
        performanceResponse,
        supportResponse,
      ] = await Promise.all([
        API.get('merchant').catch(() => ({ data: { data: [] } })),
        API.get('devices').catch(() => ({ data: { data: [] } })),
        API.get('orders').catch(() => ({ data: { data: [] } })),
        API.get('analytics/admin/revenue').catch(() => ({ data: { total: 0 } })),
        API.get('analytics/admin/transactions').catch(() => ({ data: { data: [] } })),
        API.get('analytics/admin/performance').catch(() => ({
          data: {
            conversionRate: 0,
            averageOrderValue: 0,
            customerSatisfaction: 0,
            systemUptime: 0,
          },
        })),
        API.get('analytics/admin/support').catch(() => ({ data: { tickets: 0 } })),
      ]);

      const merchantsData = merchantsResponse.data.data || [];
      const devicesData = devicesResponse.data.data || [];
      const ordersData = ordersResponse.data.data || [];
      const revenueData = revenueResponse.data.total || 0;
      const transactionsData = transactionsResponse.data.data || [];
      const performanceData = performanceResponse.data || {};
      const supportData = supportResponse.data || {};

      return {
        merchants: merchantsData,
        devices: devicesData,
        totalMerchants: merchantsData.length,
        activeDevices: devicesData.length,
        totalOrders: ordersData.length,
        totalRevenue: revenueData,
        recentTransactions: transactionsData.slice(0, 10),
        performanceMetrics: {
          conversionRate: performanceData.conversionRate || 0,
          averageOrderValue: performanceData.averageOrderValue || 0,
          customerSatisfaction: performanceData.customerSatisfaction || 0,
          systemUptime: performanceData.systemUptime || 0,
        },
        supportTickets: supportData.tickets || 0,
        lastUpdated: new Date().toISOString(),
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch admin reports';
      notification.error('Failed to Fetch Reports', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const adminReportsSlice = createSlice({
  name: 'adminReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetReports: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminReports.fulfilled, (state, action) => {
        state.loading = false;
        state.merchants = action.payload.merchants;
        state.devices = action.payload.devices;
        state.totalMerchants = action.payload.totalMerchants;
        state.activeDevices = action.payload.activeDevices;
        state.totalOrders = action.payload.totalOrders;
        state.totalRevenue = action.payload.totalRevenue;
        state.recentTransactions = action.payload.recentTransactions;
        state.performanceMetrics = action.payload.performanceMetrics;
        state.supportTickets = action.payload.supportTickets;
        state.lastUpdated = action.payload.lastUpdated;
        // Don't show success notification on initial load or auto-refresh
      })
      .addCase(fetchAdminReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetReports } = adminReportsSlice.actions;
export default adminReportsSlice.reducer;
